import ArrowBack from '@mui/icons-material/ArrowBack';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DateRangeSelect } from '@/components/common/date/DateRangeSelect';
import { MAX_DATE_RANGE_DAYS } from '@/features/time-series-chart/config/dateRangeConfig';
import type { DiagramSeriesGroup } from '@/features/time-series-chart/types/DiagramSeries.types';
import type { TimeSeriesChartDateRange } from '@/features/time-series-chart/types/TimeSeriesChartDateRange.types';

import { DiagramSeriesSelector } from '../diagram-series-selector/DiagramSeriesSelector';
import {
  DateSelector,
  DateSelectorHeader,
  SelectionPanelRoot,
  SeriesSelectorContainer,
} from './TimeSeriesChartSelectionPanel.style';

export type TimeSeriesChartSelectionPanelProps = {
  seriesGroups: DiagramSeriesGroup[];
  getSeriesColor: (seriesId: string) => string;
  selectedSeriesIds: string[];
  onSelectedSeriesIdsChange: (seriesIds: string[]) => void;
  isSeriesLoading?: boolean;
  isSeriesError?: boolean;
  onSeriesRetry: () => void;
  selectedDates: TimeSeriesChartDateRange | undefined;
  onDateChange: (from: Date, to: Date) => void;
  onBack: () => void;
};

const NO_DATES = { from: null, to: null };

export function TimeSeriesChartSelectionPanel({
  seriesGroups,
  getSeriesColor,
  selectedSeriesIds,
  onSelectedSeriesIdsChange,
  isSeriesLoading,
  isSeriesError,
  onSeriesRetry,
  selectedDates,
  onDateChange,
  onBack,
}: TimeSeriesChartSelectionPanelProps) {
  const { t } = useTranslation();

  return (
    <SelectionPanelRoot data-qa-element-id="time-series-chart-selection-panel">
      <DateSelector data-qa-element-id="time-series-chart-date-selector">
        <DateSelectorHeader>
          <IconButton aria-label={t('timeSeriesChart.channelSelector.back')} onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="h2" data-qa-element-id="time-series-chart-date-selector-title">
            {t('timeSeriesChart.dateSelector.title')}
          </Typography>
        </DateSelectorHeader>
        <Typography
          variant="helperText"
          color="text.secondary"
          data-qa-element-id="time-series-chart-date-selector-description"
        >
          {t('timeSeriesChart.dateSelector.description', { maxDays: MAX_DATE_RANGE_DAYS })}
        </Typography>
        <DateRangeSelect
          setSelectedDates={onDateChange}
          currentValue={selectedDates ?? NO_DATES}
          disabled={!selectedDates}
          maxRangeDays={MAX_DATE_RANGE_DAYS}
          clearEndOnStartSelection
        />
      </DateSelector>
      <SeriesSelectorContainer>
        <DiagramSeriesSelector
          groups={seriesGroups}
          getSeriesColor={getSeriesColor}
          selectedSeriesIds={selectedSeriesIds}
          onSelectedSeriesIdsChange={onSelectedSeriesIdsChange}
          isLoading={isSeriesLoading}
          isError={isSeriesError}
          onRetry={onSeriesRetry}
        />
      </SeriesSelectorContainer>
    </SelectionPanelRoot>
  );
}
